/**
 * Client for the `pick_lists` admin endpoints.
 *
 * Talks to `/api/broker_deals/pick_lists/...` — the generic dropdown-vocabulary
 * CRUD surface added in de-hub (migration 046). Each pick-list row is keyed by
 * `(table_name, field, value)` and carries a display `label`, `sort_order`, and
 * an `active` flag. The Admin → Pick Lists page (gated on `can("admin.pickLists")`
 * / BD.Admin) manages these; the backend inlines the active values into
 * `GET /metadata` as an `options` array for flat-enum select fields, so the
 * deal-detail tabs no longer hardcode those vocabularies.
 *
 * Endpoints:
 *   - GET    /broker_deals/pick_lists?table_name=&field=&include_inactive=
 *   - POST   /broker_deals/pick_lists
 *   - PUT    /broker_deals/pick_lists/{id}        (partial; exclude_unset)
 *   - DELETE /broker_deals/pick_lists/{id}
 *
 * Each method takes `{ token, userOid, ... }` and returns the raw axios
 * response — callers destructure `response.data.data` per the standard
 * de-hub envelope ({ success, message, data, count }).
 *
 * Mirrors the `brokerService.js` default-export pattern so the page can mock
 * the module wholesale in tests.
 */
import dataService from "./dataservice";

const BASE = "/broker_deals/pick_lists";

/**
 * GET /api/broker_deals/pick_lists?table_name=&field=&include_inactive=
 *
 * All filters are optional. Backend default returns ACTIVE-only rows; opt in
 * via `includeInactive=true` to surface deactivated values (the admin grid's
 * "Show inactive" toggle). `tableName` / `field` narrow to a single vocabulary.
 *
 * @param {object} args
 * @param {string} args.token
 * @param {string} args.userOid
 * @param {string}  [args.tableName]
 * @param {string}  [args.field]
 * @param {boolean} [args.includeInactive=false]
 */
const listPickLists = ({
  token,
  userOid,
  tableName,
  field,
  includeInactive = false,
}) => {
  const params = { include_inactive: includeInactive };
  if (tableName) params.table_name = tableName;
  if (field) params.field = field;
  return dataService.request("get", BASE, token, userOid, undefined, {
    params,
  });
};

/**
 * POST /api/broker_deals/pick_lists
 *
 * Body shape: { table_name (req), field (req), value (req), label?,
 * sort_order?, active? }. A duplicate `(table_name, field, value)` collides on
 * the server's UNIQUE constraint and returns 409.
 */
const createPickList = ({ token, userOid, body }) =>
  dataService.postEndpoint(BASE, token, userOid, body);

/**
 * PUT /api/broker_deals/pick_lists/{id}
 *
 * Partial update — only keys present in `body` are written
 * ({ value?, label?, sort_order?, active? }).
 */
const updatePickList = ({ token, userOid, id, body }) =>
  dataService.updateEndpoint(
    `${BASE}/${encodeURIComponent(id)}`,
    token,
    userOid,
    body
  );

/**
 * DELETE /api/broker_deals/pick_lists/{id}
 */
const deletePickList = ({ token, userOid, id }) =>
  dataService.deleteEndpoint(
    `${BASE}/${encodeURIComponent(id)}`,
    token,
    userOid
  );

const pickListsService = {
  listPickLists,
  createPickList,
  updatePickList,
  deletePickList,
};

export default pickListsService;
